import { PillButton } from '@parsleyai/ui'
import { RotateCw } from 'lucide-react'
import { Component, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import PageLayout from '@/components/PageLayout'

// A lazy route whose chunk never arrives rejects inside Suspense, and React unmounts the whole tree
// rather than that one route — a blank page. The chunk can go missing for reasons the app doesn't
// control: a content blocker, a dropped connection, a deploy that rotated the hashed filenames.

function RouteErrorFallback() {
  const { t } = useTranslation()

  return (
    <PageLayout>
      <div className="grid flex-1 place-items-center py-10">
        <div className="w-full max-w-[520px] rounded-[20px] bg-card px-8 py-9 text-center shadow-card">
          <h1 className="font-brand font-normal text-2xl text-foreground tracking-[-.015em] [text-wrap:pretty]">
            {t('routeError.headline')}
          </h1>
          <p className="mx-auto mt-2.5 max-w-[44ch] text-base text-muted-foreground [text-wrap:pretty]">{t('routeError.lede')}</p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-2.5">
            <PillButton variant="primary" size="lg" icon={<RotateCw />} onClick={() => window.location.reload()}>
              {t('routeError.retry')}
            </PillButton>
            <PillButton variant="ghost" size="lg" asChild>
              <Link to="/home">{t('routeError.home')}</Link>
            </PillButton>
          </div>
        </div>
      </div>
    </PageLayout>
  )
}

interface BoundaryProps {
  /** Changing this clears the error, so navigating away is a way out. */
  resetKey: string
  children: ReactNode
}

interface BoundaryState {
  failed: boolean
  resetKey: string
}

class Boundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { failed: false, resetKey: this.props.resetKey }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  static getDerivedStateFromProps(props: BoundaryProps, state: BoundaryState): BoundaryState | null {
    if (props.resetKey === state.resetKey) return null
    return { failed: false, resetKey: props.resetKey }
  }

  render() {
    return this.state.failed ? <RouteErrorFallback /> : this.props.children
  }
}

export default function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()

  return <Boundary resetKey={pathname}>{children}</Boundary>
}
